//#region assets/standard-actions-override.ts
/**
* Horizon overrides for Shopify.actions:
* - updateCart: emit events from the cart drawer scope (kept for section morphing).
* - openCart: open the Poelgeest cart flyout instead of Horizon's theme-drawer.
*
* Hijacks ThemeDrawer.prototype.open because Shopify standard-actions and
* cart-drawer-component call theme-drawer#cart-drawer.open() directly.
*/
var VP_CART_PANEL_SELECTOR = "[data-vp-cart-panel]";
/** @returns {boolean} */
function usesVpCartFlyout() {
	return Boolean(document.querySelector(VP_CART_PANEL_SELECTOR));
}
/**
* Instantly dismiss Horizon's cart dialog (skip close animation).
* Animated close removes [open] before dialog.close(), which can briefly
* reveal the top-layer dialog over the VP flyout.
* @returns {void}
*/
function forceCloseHorizonCartDrawer() {
	const drawer = document.querySelector("theme-drawer#cart-drawer");
	if (!(drawer instanceof HTMLElement)) return;
	const dialog = drawer.querySelector("dialog");
	drawer.removeAttribute("open");
	drawer.style.removeProperty("--drawer-stack-order");
	if (dialog instanceof HTMLDialogElement) {
		dialog.classList.remove("theme-drawer__dialog--opening", "theme-drawer__dialog--opening-inline-start", "theme-drawer__dialog--closing", "theme-drawer__dialog-instant");
		if (dialog.open) dialog.close();
	}
	document.querySelector(".page-wrapper")?.classList.remove("page-wrapper--drawer-open");
}
/** @returns {Promise<boolean>} */
async function openVpCartFlyout() {
	forceCloseHorizonCartDrawer();
	for (let attempt = 0; attempt < 40; attempt += 1) {
		const flyouts = window.vpHeaderFlyouts;
		if (flyouts?.openCart) {
			await flyouts.openCart();
			forceCloseHorizonCartDrawer();
			return true;
		}
		await new Promise((resolve) => requestAnimationFrame(resolve));
	}
	return false;
}
/** @returns {boolean} */
function configureActions() {
	if (!usesVpCartFlyout()) return false;
	const actions = window.Shopify?.actions;
	if (!actions) return false;
	actions.updateCart.configure({ eventTarget: () => document.querySelector("theme-drawer#cart-drawer") ?? document });
	actions.openCart.configure({ async handler() {
		if (await openVpCartFlyout()) return;
		window.location.href = Theme.routes.cart_url || "/cart";
	} });
	return true;
}
function hijackThemeDrawerPrototype() {
	if (!usesVpCartFlyout()) return;
	const ThemeDrawer = customElements.get("theme-drawer");
	if (!ThemeDrawer?.prototype || ThemeDrawer.prototype.__vpCartHijacked) return;
	const originalOpen = ThemeDrawer.prototype.open;
	const originalToggle = ThemeDrawer.prototype.toggle;
	if (typeof originalOpen !== "function") return;
	ThemeDrawer.prototype.__vpCartHijacked = true;
	ThemeDrawer.prototype.open = function(...args) {
		if (this.id === "cart-drawer" && usesVpCartFlyout()) {
			openVpCartFlyout();
			return;
		}
		return originalOpen.apply(this, args);
	};
	if (typeof originalToggle === "function") ThemeDrawer.prototype.toggle = function(...args) {
		if (this.id === "cart-drawer" && usesVpCartFlyout()) {
			const flyouts = window.vpHeaderFlyouts;
			if (flyouts?.isOpen) {
				flyouts.closeAll?.();
				forceCloseHorizonCartDrawer();
				return;
			}
			openVpCartFlyout();
			return;
		}
		return originalToggle.apply(this, args);
	};
	forceCloseHorizonCartDrawer();
}
function boot() {
	if (!usesVpCartFlyout()) return;
	hijackThemeDrawerPrototype();
	configureActions();
	forceCloseHorizonCartDrawer();
}
function waitForShopifyActions(attempt = 0) {
	if (!usesVpCartFlyout()) return;
	if (configureActions() || attempt > 200) return;
	requestAnimationFrame(() => waitForShopifyActions(attempt + 1));
}
document.addEventListener("DOMContentLoaded", boot, { once: true });
customElements.whenDefined("theme-drawer").then(boot);
requestAnimationFrame(boot);
waitForShopifyActions();
//#endregion
